import React, { useMemo } from 'react';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { getTokens, ThemeMode } from '../tokens';
import { tokensToCssVars } from './cssVars';
import { buildMuiTheme } from './muiBridge';

type Props = {
  mode: ThemeMode;
  children: React.ReactNode;
};

/**
 * Forces a theme mode on a subtree without touching the app-wide theme.
 * CSS variables are set on a display:contents wrapper so they cascade to
 * the children without adding a layout box, and MUI components inside get
 * a matching theme via MuiThemeProvider.
 *
 * Use for surfaces that must always match one look (e.g. the onboarding
 * funnel mirrors the dark tribed.io landing) regardless of the admin's
 * light/dark preference.
 */
export const ThemeScope: React.FC<Props> = ({ mode, children }) => {
  const tokens = useMemo(() => getTokens(mode), [mode]);
  const muiTheme = useMemo(() => buildMuiTheme(tokens), [tokens]);
  const style = useMemo(
    () =>
      ({
        ...tokensToCssVars(tokens),
        display: 'contents',
        colorScheme: mode,
      } as React.CSSProperties),
    [tokens, mode]
  );

  return (
    <MuiThemeProvider theme={muiTheme}>
      <div style={style} data-theme={mode}>
        {children}
      </div>
    </MuiThemeProvider>
  );
};
